// Refresh cookies are shared by tabs. Keep cookie-changing requests in one
// origin-wide queue; access tokens and user data stay in memory, never storage.
export function createSessionCoordinator({ name, onChange, locks, channel, storage, now = Date.now } = {}) {
  let session = null;
  let revision = 0;
  let known = false;
  const waiters = new Set();
  const markerKey = `${name}:revision`;

  function storedRevision() {
    try { return Number(storage?.getItem(markerKey)) || 0; } catch { return 0; }
  }

  function accept(value, version) {
    if (version <= revision || version < storedRevision()) return;
    revision = version;
    known = true;
    session = value;
    onChange?.(session);
    for (const resolve of waiters) resolve();
  }

  function publish(value) {
    const version = Math.max(now(), revision + 1, storedRevision() + 1);
    try { storage?.setItem(markerKey, String(version)); } catch { /* Private browsing may deny storage. */ }
    accept(value, version);
    channel?.postMessage({ type: 'session', session: value, revision: version });
    return value;
  }

  if (channel) channel.onmessage = ({ data }) => {
    if (data?.type === 'request') {
      if (known && revision >= storedRevision()) {
        channel.postMessage({ type: 'session', session, revision });
      }
    } else if (data?.type === 'session' && Number.isSafeInteger(data.revision)
      && (data.session === null || (typeof data.session?.accessToken === 'string' && typeof data.session?.user?.id === 'string'))) {
      accept(data.session, data.revision);
    }
  };

  async function synchronize() {
    if (!channel || (known && revision >= storedRevision())) return;
    // A newly opened tab can obtain the current in-memory session from a peer.
    // If no peer remains, refresh with the shared HttpOnly cookie under the lock.
    await new Promise(resolve => {
      const done = () => { clearTimeout(timer); waiters.delete(done); resolve(); };
      const timer = setTimeout(done, 120);
      waiters.add(done);
      channel.postMessage({ type: 'request' });
    });
  }

  const exclusive = action => locks?.request ? locks.request(name, action) : action();

  return {
    current: () => session,
    async refresh(action, attemptedToken) {
      const before = revision;
      return exclusive(async () => {
        await synchronize();
        // Another tab already refreshed this credential, logged out, or signed
        // into another account while this request waited. Do not rotate again.
        if ((known && session === null && revision > before)
          || (session?.accessToken && session.accessToken !== attemptedToken)) return session;
        try { return publish(await action()); }
        catch (error) {
          if (error.response?.status === 401) publish(null);
          throw error;
        }
      });
    },
    async change(action, { logout = false } = {}) {
      return exclusive(async () => {
        try {
          const value = await action();
          return publish(logout ? null : value);
        } catch (error) {
          if (logout) publish(null);
          throw error;
        }
      });
    },
    dispose() { channel?.close(); }
  };
}

export function browserSessionOptions(baseURL, enabled = true) {
  const name = `nur:session:${baseURL}`;
  if (!enabled || typeof window === 'undefined') return { name };
  let storage;
  try { storage = window.localStorage; } catch { /* Tokens do not depend on storage access. */ }
  let channel;
  try { channel = new BroadcastChannel(name); } catch { /* Web Locks still serialize refresh cookies. */ }
  return { name, storage, channel, locks: navigator.locks };
}
